import { BrewIcon } from '@/components/common/BrewIcon';
import { BREW_TYPES, type BrewType } from '@/lib/brewTypes';

interface BrewSelectorProps {
  value: BrewType | null;
  onChange: (value: BrewType | null) => void;
  testIdPrefix?: string;
}

// Brew method picker for a check-in. Brew method is optional and independently
// togglable (clicking the active pill clears it), so these are toggle buttons
// (`aria-pressed`) in a labelled group — not radios, which have no cleared state
// and would promise arrow-key roving this component doesn't implement.
export function BrewSelector({
  value,
  onChange,
  testIdPrefix = 'check-in-brew',
}: BrewSelectorProps) {
  return (
    <div role="group" aria-label="Brew method" className="flex flex-wrap justify-center gap-2">
      {BREW_TYPES.map((brew) => {
        const active = value === brew.id;
        return (
          <button
            key={brew.id}
            type="button"
            aria-pressed={active}
            data-testid={`${testIdPrefix}-${brew.id}`}
            onClick={() => onChange(active ? null : brew.id)}
            // Pin border-2 on both states so toggling only swaps colour, never
            // the box size (a 1px border would nudge the row layout).
            className={`inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-sm font-bold transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-wolisho-red ${
              active
                ? 'border-wolisho-brown bg-wolisho-brown text-white'
                : 'border-wolisho-brown/15 bg-transparent text-wolisho-brown hover:border-wolisho-brown/40'
            }`}
          >
            <BrewIcon type={brew.id} className="h-4 w-4" />
            {brew.label}
          </button>
        );
      })}
    </div>
  );
}
